import { useMemo } from "react";
import useFormatPrice from "../../../hooks/useFormatPrice.js";
import useUserAgent from "../../../hooks/useUserAgent.js";
import { pricingUtils } from "@flights/web-api-utils-universal";
import useRouteName from "../../../hooks/useRouteName.js";
export const useUnifiedPriceTagController = (props) => {
    const { isMobile } = useUserAgent();
    const routeName = useRouteName();
    const { formatPrice } = useFormatPrice();
    const { price, priceAlignment, priceTranslations = [], pricePerAdult, pricePerAdultExplanationTranslations = [], pricePerAdultTranslations = [], priceProps, shouldRoundPrice, shouldShowPricePerAdult = false, strikethroughPrice, strikethroughPricePosition = "top" } = props;
    const formattedPrice = useMemo(() => {
        const priceValue = shouldShowPricePerAdult && pricePerAdult ? pricePerAdult : price;
        const priceFormatted = formatPrice(priceValue, shouldRoundPrice ? { decimalPlaces: 0 } : {});
        return (shouldShowPricePerAdult && pricePerAdult ? pricePerAdultTranslations : priceTranslations).reduce((acc, translate) => translate(acc), priceFormatted);
    }, [
        price,
        pricePerAdult,
        priceTranslations,
        pricePerAdultTranslations,
        shouldShowPricePerAdult,
        shouldRoundPrice,
        formatPrice
    ]);
    const formattedStrikethroughPrice = useMemo(() => {
        return !!strikethroughPrice ? formatPrice(strikethroughPrice, shouldRoundPrice ? { decimalPlaces: 0 } : {}) : "";
    }, [formatPrice, shouldRoundPrice, strikethroughPrice]);
    const priceWrapperStackProps = useMemo(() => ({
        justifyContent: priceAlignment || "start"
    }), [priceAlignment]);
    const priceStackProps = useMemo(() => {
        if (!strikethroughPrice) {
            return {};
        }
        const direction = strikethroughPricePosition === "top"
            ? "column"
            : strikethroughPricePosition === "bottom"
                ? "column-reverse"
                : strikethroughPricePosition === "start"
                    ? "row"
                    : "row-reverse";
        const alignItems = ["top", "bottom"].includes(strikethroughPricePosition)
            ? priceAlignment === "end"
                ? priceAlignment
                : "start"
            : "baseline";
        const getGapValue = () => {
            if (["top", "bottom"].includes(strikethroughPricePosition))
                return 0;
            if (routeName.includes("flightdetails"))
                return isMobile ? 0 : 2;
            return 1;
        };
        const gap = getGapValue();
        return { ...priceProps, direction, alignItems, gap };
    }, [priceAlignment, priceProps, strikethroughPrice, strikethroughPricePosition, isMobile, routeName]);
    const priceTextProps = useMemo(() => ({
        variant: priceProps?.variant || "strong_1",
        tagName: priceProps?.tagName || "div"
    }), [priceProps]);
    const pricePerAdultExplanation = useMemo(() => {
        const priceFormatted = formatPrice(price, shouldRoundPrice ? { decimalPlaces: 0 } : {});
        return pricePerAdultExplanationTranslations.reduce((acc, translate) => translate(acc), priceFormatted);
    }, [formatPrice, price, pricePerAdultExplanationTranslations, shouldRoundPrice]);
    const pricePerAdultExplanationStrikethroughInline = useMemo(() => !(isMobile && pricingUtils.isPriceWithLargeNumber(price)), [isMobile, price]);
    return {
        formattedPrice,
        formattedStrikethroughPrice,
        priceWrapperStackProps,
        priceStackProps,
        priceTextProps,
        pricePerAdultExplanation,
        pricePerAdultExplanationStrikethroughInline
    };
};
